import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { H2, Row, Text, Loading } from '@commonsku/styles';
import NewProduct from '../../NewProduct';
import { skuFont } from '../../helpers/styles';
import { ProductGridProps } from './types';
import { getSubTitle } from './utils';

const GAP_SIZE = 28;

const LoadingWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 100px 20px;
`;

const NoResultsWrapper = styled.div`
  width: 100%;
  text-align: center;
  padding: 100px 20px;
`;

const NoResultsTitle = styled(H2)`
  font-size: 24px;
  font-weight: 600;
  font-family: ${skuFont};
`;

const NoResultsSubtitle = styled(Text)`
  font-size: 24px;
  font-weight: 400;
  font-family: ${skuFont};
`;

const ProductGridRow = styled(Row)<{ $contentWidth: number }>`
  gap: ${GAP_SIZE}px;
  overflow: auto;
  width: ${({ $contentWidth }) => $contentWidth}px;
`;

const InfiniteHitsSentinel = styled.div.attrs({
  className: 'ais-InfiniteHits-sentinel',
  'aria-hidden': true,
})`
  height: 20px;
  width: 100%;
  background-color: transparent;
  flex-shrink: 0;
`;

const DefaultNoResults: React.FC<{ isMinGreaterThanMax: boolean }> = ({ isMinGreaterThanMax }) => {
  const title = isMinGreaterThanMax ? 'Invalid price range' : 'We can\'t find any products that match your search.';
  const subtitle = isMinGreaterThanMax ? 'Make sure your \'From\' value is less than \'To\'.' : 'Try changing the filters on the left, or the search above.';
  return (
    <NoResultsWrapper>
      <NoResultsTitle>{title}</NoResultsTitle>
      <NoResultsSubtitle>{subtitle}</NoResultsSubtitle>
    </NoResultsWrapper>
  );
};

const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  isLastPage,
  onLoadMore,
  loading,
  isProductsPage = false,
  isSelected,
  onClickProduct,
  onClickProductAction,
  contentWidth,
  range,
  renderNoResults,
  productType,
  isItemLoading,
  isLoading,
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isMinGreaterThanMax = range ? (range.min && range.max && Number(range.min) > Number(range.max)) : false;

  useEffect(() => {
    if (sentinelRef.current !== null) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLastPage) {
            onLoadMore();
          }
        });
      }, {
        threshold: [0, 0.1],
        rootMargin: '100px 0px',
      });

      observer.observe(sentinelRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [isLastPage, onLoadMore]);

  const noResultsContent = renderNoResults
    ? renderNoResults()
    : <DefaultNoResults isMinGreaterThanMax={!!isMinGreaterThanMax} />;
  const loadingSet = new Set(loading);
  const emptyContent = isLoading ? <LoadingWrapper><Loading /></LoadingWrapper> : noResultsContent;

  return (
    <ProductGridRow $contentWidth={contentWidth}>
      {products.length > 0 && !isMinGreaterThanMax ? products.map((product) => {
        const onLoading = isItemLoading ? isItemLoading(product) : loadingSet.has(product.id);
        return (
          <div key={product.id} data-test-name="product-cards">
            <NewProduct
              title={product.name}
              sku={product.supplierCode}
              subtitle={getSubTitle(product.minPrice, product.maxPrice)}
              description={product.divisionName}
              product={product._raw}
              loading={onLoading}
              loaded={!isProductsPage && isSelected(product)}
              isProductsPage={isProductsPage}
              type={productType}
              supplier_popup={false}
              onClickProduct={() => onClickProduct(product)}
              onClickProductAction={() => {
                if (onLoading) return;
                onClickProductAction(product);
              }}
            />
          </div>
        );
      }) : emptyContent}
      <InfiniteHitsSentinel ref={sentinelRef} />
    </ProductGridRow>
  );
};

export default ProductGrid;
export { GAP_SIZE };
